// Minimal history-based client router. Pages register a matcher; navigate()
// pushes state and re-renders. SPA fallback (public/_redirects) serves
// index.html for deep links so these paths work on first load too.

export type RouteRenderer = (root: HTMLElement, params: Record<string, string>) => void;

interface Route {
  pattern: RegExp;
  keys: string[];
  render: RouteRenderer;
}

const routes: Route[] = [];
let notFound: RouteRenderer | null = null;
let rootEl: HTMLElement | null = null;

// Compile a path pattern like "/e/:id/manage/:token" into a regex + key list.
function compile(path: string): { pattern: RegExp; keys: string[] } {
  const keys: string[] = [];
  // Build the pattern segment by segment: a ":name" segment becomes a capture
  // group, every other segment is escaped and kept literal.
  const re = path
    .split('/')
    .map((seg) => {
      if (seg.startsWith(':')) {
        keys.push(seg.slice(1));
        return '([^/]+)';
      }
      return seg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    })
    .join('/');
  return { pattern: new RegExp(`^${re}/?$`), keys };
}

export function route(path: string, render: RouteRenderer): void {
  const { pattern, keys } = compile(path);
  routes.push({ pattern, keys, render });
}

export function setNotFound(render: RouteRenderer): void {
  notFound = render;
}

export function navigate(path: string): void {
  if (path !== location.pathname + location.search) {
    history.pushState({}, '', path);
  }
  render();
}

function render(): void {
  if (!rootEl) return;
  const path = location.pathname;
  for (const r of routes) {
    const m = r.pattern.exec(path);
    if (m) {
      const params: Record<string, string> = {};
      r.keys.forEach((k, i) => (params[k] = decodeURIComponent(m[i + 1])));
      rootEl.scrollTo?.(0, 0);
      window.scrollTo(0, 0);
      r.render(rootEl, params);
      return;
    }
  }
  if (notFound) notFound(rootEl, {});
}

export function startRouter(root: HTMLElement): void {
  rootEl = root;
  window.addEventListener('popstate', render);
  // Intercept same-origin link clicks marked with data-link for SPA navigation.
  document.addEventListener('click', (e) => {
    const a = (e.target as HTMLElement).closest<HTMLAnchorElement>('a[data-link]');
    if (!a) return;
    const href = a.getAttribute('href');
    if (!href || href.startsWith('http')) return;
    e.preventDefault();
    navigate(href);
  });
  render();
}
